import { useAuth0 } from '@auth0/auth0-react'
import { ServiceStatus, getServiceStatus } from '@infraloop/services'
import { useQuery } from '@tanstack/react-query'

const serviceStatusKey = ['serviceStatus']

/**
 * Fetches the backend service status. Runs as early as possible (does not require an
 * authenticated user) so the app can block on it before rendering any route, and so the
 * dashboard can render its notification strips from the same signal.
 *
 * Waits for Auth0 to settle, then includes the access token when a user is present
 * (mirroring the editor / platform), otherwise hits the unauthenticated endpoint.
 */
const useServiceStatus = () => {
  const { isLoading: isAuthLoading, isAuthenticated, getAccessTokenSilently } = useAuth0()

  const { data: serviceStatus, isLoading } = useQuery<ServiceStatus>({
    queryKey: serviceStatusKey,
    enabled: !isAuthLoading,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const token = isAuthenticated ? await getAccessTokenSilently() : undefined
      return getServiceStatus(token)
    },
  })

  return {
    serviceStatus,
    // Block while Auth0 is settling or the status request is in flight.
    isLoading: isAuthLoading || isLoading,
  }
}

export default useServiceStatus
